/**
 * Employment change-log vocabulary (ADR-145).
 *
 * The FIELD TAXONOMY is not here on purpose: which fields exist and which
 * group each belongs to is served by GET /reports/employment-changes/
 * field-catalog, from the one definition in the report service. The client
 * restating it is exactly how "what is a contract term" acquires a second
 * answer (ADR-145 §5.2). What lives here is presentation vocabulary — the
 * words for things the server names but does not label.
 *
 * Shared by the Employment Change Log report and the ADR-143 admin view, so
 * the two cannot label the same field or detector differently.
 */

export const FIELD_GROUP_LABELS = {
  term: 'Contract terms',
  addendum: 'Amendable, not a term',
  recordOnly: 'Change-record only',
  derived: 'Derived',
};

export const FIELD_GROUP_HINTS = {
  term: 'Integrity-checked — an unexplained movement here is a finding',
  addendum: 'Recorded as an administrative addendum, never a term',
  recordOnly: 'Observed, never linked to an explaining event',
  derived: 'Detected by the nightly sweep — never carries an effective date',
};

export const DETECTOR_OPTIONS = [
  { value: 'amendment-applier', label: 'Amendment applier', hint: 'Stamped as an amendment is applied' },
  { value: 'episode-update-hook', label: 'Episode update hook', hint: 'A term written outside an amendment' },
  { value: 'employee-update-hook', label: 'Employee update hook', hint: 'A person fact changed on the employee record' },
  { value: 'position-verb', label: 'Position verb', hint: 'A position or grade leg was written' },
  { value: 'nightly-sweep', label: 'Nightly sweep', hint: 'Derived movement, noticed at 10:00 on weekdays' },
  { value: 'resign-machinery', label: 'Resign machinery', hint: 'The contract\u2019s scheduled end was created, moved, or withdrawn' },
  { value: 'hire-machinery', label: 'Hire machinery', hint: 'A contract started, was replaced, or had its start delayed' },
  // ADR-164 D2 — the position reconciler's two kinds of write when no
  // amendment explains it: a scheduled change whose day came (a movement), and
  // a stored value catching up with history it already had (a correction —
  // shown muted, never counted as a change).
  { value: 'position-schedule', label: 'Scheduled position change', hint: 'A position or grade change scheduled in advance took effect on its day' },
  { value: 'position-repair', label: 'Stored value corrected', hint: 'The stored position or grade caught up with history it already had — a correction, not a change' },
  // ADR-168 D6.2 — an admin correcting a CLOSED contract: history repaired,
  // not moved. A correction detector server-side (muted, never counted).
  { value: 'closed-episode-correction', label: 'Closed contract corrected', hint: 'An admin corrected a closed contract (dates, end reason, terms) — a correction, not a change' },
  // ADR-169 D5 — a new position entry opened with the position and grade
  // already in effect (e.g. after a closed one). Nothing moved; muted.
  { value: 'position-verb-new-leg', label: 'Position entry reopened', hint: 'A new position entry was added with the same position and grade as before — traced, not a change' },
];

export const DETECTOR_LABELS = DETECTOR_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }), {}
);

export const ACTOR_TYPE_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'User', label: 'Human' },
  { value: 'System', label: 'Machine' },
];

// ADR-174 §7 — labelled "Contract ownership" on the page: the name
// "Contract" now belongs to the filter that picks a contract.
export const OWNERSHIP_OPTIONS = [
  { value: '', label: 'Any owner' },
  { value: 'radar', label: 'Radar-owned' },
  { value: 'sync', label: 'Sync-stamped' },
];

// ADR-146 §3.1 — the link generalized from "amendment" to "explaining
// event": an amendment, or the resign event that carried a scheduled-end
// movement. Unexplained now means a term moved with NO event behind it.
export const EXPLANATION_OPTIONS = [
  { value: '', label: 'All records' },
  { value: 'explained', label: 'Explained by an event' },
  { value: 'unexplained', label: 'Unexplained term movement' },
];

export const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 400, label: 'Full retention (400 days)' },
];

// "All statuses" is the default and it INCLUDES departed people (ADR-145
// §5.1) — the most valuable question a change log answers is what happened to
// someone's contract before they left, and it is unanswerable if they fall out
// of the default population. The control carries a tooltip saying so, because
// it is the one place this report deliberately differs from its siblings.
export const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'employed', label: 'Employed' },
  { value: 'joining', label: 'Joining' },
  { value: 'leaving', label: 'Leaving' },
  { value: 'resigned', label: 'Resigned' },
];

/**
 * The default view IS the product (ADR-145 §6): last 30 days, the viewer's
 * scope, everything shown, machine rows muted rather than hidden.
 */
// ADR-112 — the lane selector, labelled "Scheduled changes" (the domain term
// the cards and cancel dialogs already use). NOT "amendments": applied
// amendments are already IN the report as their explained change records —
// what the lane adds is the scheduled changes that have not (or never) taken
// effect, so "Hidden" is accurate. Hidden by default (§8 decision 2). The
// stored values are stable — labels are display-only.
export const AMENDMENTS_LANE_OPTIONS = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'scheduled', label: 'Upcoming' },
  // Both capitalized deliberately, against this file's sentence-case norm:
  // the option names the lane's two SECTIONS (the "Upcoming" and "Cancelled"
  // sub-headings), not a phrase. Lowercase reads as one qualified category.
  { value: 'all', label: 'Upcoming + Cancelled' },
];

export const AMENDMENT_CANCEL_CAUSE_LABELS = {
  operator: 'Cancelled by an operator',
  'contract-close': 'Died at contract close',
  sweep: 'Cancelled by the sweep',
};

export const DEFAULT_FILTERS = {
  clusterId: '',
  includeSubUnits: true,
  businessUnitId: '',
  buScope: 'direct',
  employeeId: '',
  representativeId: '',
  reportingScope: 'direct',
  organizationalStatus: '',
  fieldGroups: [],
  fields: [],
  changedTo: '',
  detectors: [],
  actorTypes: [],
  actorId: '',
  ownership: '',
  // ADR-174 §7 — the contract filters
  contractNumber: '',
  employmentEpisodeIds: [],
  concurrentOnly: false,
  explanation: '',
  windowDays: 30,
  effectiveOnly: false,
  orderDirection: 'desc',
  // ADR-112 — the amendments lane (hidden | scheduled | all)
  amendmentsLane: 'hidden',
};

